/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  serverTimestamp
} from "firebase/firestore";

const state = {
  presetDataSets: [],
  loadingPresetDataSets: false
};

const actions = {
  async loadPresetDataSets({ commit, getters }) {
    commit("setLoadingPresetDataSets", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const presetDataSetsRef = collection(programRef, "presetDataSets");
      const querySnapshot = await getDocs(presetDataSetsRef);

      const presetDataSets = [];

      // Process each dataset and check for records
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        const hasData = await checkForRecords(docSnapshot.id);

        presetDataSets.push({
          id: docSnapshot.id,
          hasData,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      }

      // Helper function to check if dataset has records
      async function checkForRecords(datasetId) {
        try {
          const dataSetRef = doc(presetDataSetsRef, datasetId);
          const dataRef = collection(dataSetRef, "data");
          const limitedQuery = query(dataRef, limit(1));
          const snapshot = await getDocs(limitedQuery);
          return !snapshot.empty;
        } catch (error) {
          console.error(
            `Error checking records for dataset ${datasetId}:`,
            error
          );
          return false;
        }
      }

      commit("setPresetDataSets", presetDataSets);
    } catch (error) {
      console.error("Error loading preset data sets:", error);
      commit("setPresetDataSets", []);
    } finally {
      commit("setLoadingPresetDataSets", false);
    }
  },

  async getPresetDataSetById({ getters }, dataSetId) {
    if (!dataSetId) {
      console.error("DataSet ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );
      const dataSetDoc = await getDoc(dataSetRef);

      if (dataSetDoc.exists()) {
        const data = dataSetDoc.data();
        return {
          id: dataSetDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Preset data set with ID ${dataSetId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting preset data set by ID:", error);
      return null;
    }
  },

  async createPresetDataSet({ dispatch, commit, getters }, payload) {
    if (!payload || !payload.dataSetTitle) {
      throw new Error("Payload with dataSetTitle is required");
    }

    commit("setLoadingPresetDataSets", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const presetDataSetsRef = collection(programRef, "presetDataSets");

      // Check for duplicate titles
      const dataSetTitleUppercase =
        payload.dataSetTitleUppercase || payload.dataSetTitle.toUpperCase();
      const titleDupQuery = query(
        presetDataSetsRef,
        where("dataSetTitleUppercase", "==", dataSetTitleUppercase)
      );
      const titleDupSnapshot = await getDocs(titleDupQuery);

      if (!titleDupSnapshot.empty) {
        throw new Error("Data Set title is already registered");
      }

      const { id, ...presetDataSetInput } = payload; // eslint-disable-line no-unused-vars
      const dataSetData = {
        ...presetDataSetInput,
        dataSetTitleUppercase: dataSetTitleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "unknown",
        updatedBy: getters.user?.id || "unknown"
      };

      const newDataSetRef = await addDoc(presetDataSetsRef, dataSetData);

      const tempDataSet = {
        ...presetDataSetInput,
        id: newDataSetRef.id,
        dataSetTitleUppercase: dataSetTitleUppercase,
        hasData: false,
        created: new Date(),
        updated: new Date(),
        createdBy: getters.user?.id || "unknown",
        updatedBy: getters.user?.id || "unknown"
      };

      commit("createPresetDataSet", tempDataSet);
      dispatch("setSnackbar", "Data Set Created");

      return tempDataSet;
    } catch (error) {
      console.error("Error creating preset data set:", error);
      throw error.message || "Error occurred when creating data set";
    } finally {
      commit("setLoadingPresetDataSets", false);
    }
  },

  async updatePresetDataSet({ dispatch, commit, getters }, payload) {
    if (!payload || !payload.id || !payload.dataSetTitle) {
      throw new Error("Payload with id and dataSetTitle is required");
    }

    commit("setLoadingPresetDataSets", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const presetDataSetsRef = collection(programRef, "presetDataSets");
      const dataSetRef = doc(presetDataSetsRef, payload.id);

      // Verify the dataset exists
      const storedPresetDataSet = await getDoc(dataSetRef);
      if (!storedPresetDataSet.exists()) {
        throw new Error("Data Set not found");
      }

      // Check for duplicate titles if title is being updated
      const dataSetTitleUppercase =
        payload.dataSetTitleUppercase || payload.dataSetTitle.toUpperCase();
      const titleDupQuery = query(
        presetDataSetsRef,
        where("dataSetTitleUppercase", "==", dataSetTitleUppercase)
      );
      const presetDataSetDupSnapshot = await getDocs(titleDupQuery);

      if (!presetDataSetDupSnapshot.empty) {
        let duplicated = false;
        presetDataSetDupSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== payload.id) {
            duplicated = true;
          }
        });
        if (duplicated) {
          throw new Error("Data set title is already registered");
        }
      }

      const { id, ...presetDataSetInput } = payload;
      const updateData = {
        ...presetDataSetInput,
        dataSetTitleUppercase: dataSetTitleUppercase,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "unknown"
      };

      await updateDoc(dataSetRef, updateData);

      const tempDataSet = {
        ...storedPresetDataSet.data(),
        ...payload,
        dataSetTitleUppercase: dataSetTitleUppercase,
        updated: new Date(),
        updatedBy: getters.user?.id || "unknown"
      };

      commit("updatePresetDataSet", tempDataSet);

      // Reload to get fresh data including hasData status
      await dispatch("loadPresetDataSets");

      dispatch("setSnackbar", "Preset Data Set Updated");

      return tempDataSet;
    } catch (error) {
      console.error("Error updating preset data set:", error);
      throw error.message || "Error occurred when updating data set";
    } finally {
      commit("setLoadingPresetDataSets", false);
    }
  },

  async deletePresetDataSet({ dispatch, commit, getters }, dataSetId) {
    if (!dataSetId) {
      throw new Error("DataSet ID is required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );

      await deleteDoc(dataSetRef);

      commit("deletePresetDataSet", dataSetId);
      dispatch("setSnackbar", "Data set deleted");

      return true;
    } catch (error) {
      console.error("Error deleting preset data set:", error);
      throw error.message || "Error occurred when deleting data set";
    }
  },

  async checkDataSetHasRecords({ getters }, dataSetId) {
    if (!dataSetId) {
      console.error("DataSet ID is required");
      return false;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );
      const dataRef = collection(dataSetRef, "data");
      const limitedQuery = query(dataRef, limit(1));
      const snapshot = await getDocs(limitedQuery);

      return !snapshot.empty;
    } catch (error) {
      console.error("Error checking if dataset has records:", error);
      return false;
    }
  },

  setLoadingPresetDataSets({ commit }, payload) {
    commit("setLoadingPresetDataSets", payload);
  }
};

const mutations = {
  setPresetDataSets(state, payload) {
    state.presetDataSets = Array.isArray(payload) ? payload : [];
  },

  setLoadingPresetDataSets(state, payload) {
    state.loadingPresetDataSets = Boolean(payload);
  },

  createPresetDataSet(state, payload) {
    if (payload && payload.id) {
      state.presetDataSets = [...state.presetDataSets, payload];
    }
  },

  updatePresetDataSet(state, payload) {
    if (payload && payload.id) {
      const index = state.presetDataSets.findIndex(
        item => item.id === payload.id
      );
      if (index !== -1) {
        state.presetDataSets = [
          ...state.presetDataSets.slice(0, index),
          { ...state.presetDataSets[index], ...payload },
          ...state.presetDataSets.slice(index + 1)
        ];
      }
    }
  },

  deletePresetDataSet(state, dataSetId) {
    state.presetDataSets = state.presetDataSets.filter(
      item => item.id !== dataSetId
    );
  }
};

const getters = {
  presetDataSets(state) {
    return state.presetDataSets;
  },

  loadingPresetDataSets(state) {
    return state.loadingPresetDataSets;
  },

  // Enhanced getters for better data access
  getPresetDataSetById: state => id => {
    return state.presetDataSets.find(dataSet => dataSet.id === id) || null;
  },

  getPresetDataSetByTitle: state => title => {
    return (
      state.presetDataSets.find(
        dataSet => dataSet.dataSetTitle?.toLowerCase() === title.toLowerCase()
      ) || null
    );
  },

  presetDataSetsWithData(state) {
    return state.presetDataSets.filter(dataSet => dataSet.hasData === true);
  },

  presetDataSetsWithoutData(state) {
    return state.presetDataSets.filter(dataSet => dataSet.hasData === false);
  },

  presetDataSetsCount(state) {
    return state.presetDataSets.length;
  },

  presetDataSetsWithDataCount(state) {
    return state.presetDataSets.filter(dataSet => dataSet.hasData === true)
      .length;
  },

  presetDataSetsOptions(state) {
    return state.presetDataSets.map(dataSet => ({
      text: dataSet.dataSetTitle || dataSet.id,
      value: dataSet.id
    }));
  },

  presetDataSetsWithDataOptions(state) {
    return state.presetDataSets
      .filter(dataSet => dataSet.hasData === true)
      .map(dataSet => ({
        text: dataSet.dataSetTitle || dataSet.id,
        value: dataSet.id
      }));
  },

  hasPresetDataSets(state) {
    return state.presetDataSets.length > 0;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
